import { defineComponent as _defineComponent } from 'vue'
import { reactive, watch, ref } from 'vue';
import type { BaseTextElement, BoundedTextElement, FontColorOption } from '@/typings/customilyElements/canvasElements';
import type { CanvasEx } from '@/typings/canvas';
import CSlider from '@/components/shared/CSlider.vue';
import { ElementMapOption } from './types';
import { useColor } from '@/composables/color';
import { ColorObject } from '@/typings/libraries';
import { LibraryTypeEnum } from '@/utils/enums/libraryType.enum';
import { KeyValueDto } from '@/typings/keyValueDto';
import ElementLibrarySelector from '@/components/DesignStudio/ElementLibrarySelector.vue';
import { ShadowTypeEnum } from '@/utils/enums/shadowType.enum';

type TextElement = BoundedTextElement;


export default /*#__PURE__*/_defineComponent({
  __name: 'TextShadowProperties',
  props: {
    canvas: null,
    textElement: { default: undefined },
    editMode: { type: Boolean },
    cmykMode: { type: Boolean },
    isPreview: { type: Boolean, default: false }
  },
  emits: ["open-shadow-color-library-selector"],
  setup(__props: any, { emit }: { emit: ({
    (e: 'open-shadow-color-library-selector'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { canvas: CanvasEx, textElement: TextElement, editMode?: boolean, cmykMode?: boolean, isPreview: boolean };

const disabled = ref(false);
const model = reactive({
    shadowOffset: 0,
    shadowAngle: 0,
    shadowBlur: 0,
    shadowType: null as ShadowTypeEnum | null,
    shadowColorLibraryId: undefined as number | undefined,
    shadowColorOption: undefined as number | undefined,
    shadowColorsMap: new Map<number, FontColorOption>(),
});

const COLOR_TYPE = LibraryTypeEnum.Color;
const DROP_SHADOW = ShadowTypeEnum.Drop;
const BLURRED_SHADOW = ShadowTypeEnum.Blurred;
const defaultOffset = 50;
const defaultAngle = 45;
const defaultBlur = 20;

const shadowOptions = [
    {
        type: ShadowTypeEnum.Drop,
        img: require('@/assets/images/drop-shadow.svg'),
        alt: 'drop-shadow'
    },
    {
        type: ShadowTypeEnum.Blurred,
        img: require('@/assets/images/blurred-drop-shadow.svg'),
        alt: 'blurred-drop-shadow'
    }
];





watch(
    () => props.textElement,
    (element) => {
        if (!element || element.type != 'bounded-text') return;

        disabled.value = element.locked;

        model.shadowType = element.shadowType;
        model.shadowOffset = element.shadowOffset || 0;
        model.shadowAngle = element.shadowAngle || 0;
        model.shadowBlur = element.shadowBlur || 0;
        model.shadowColorOption = element.shadowColorOption;
        model.shadowColorLibraryId = element.shadowColorLibraryId;
        model.shadowColorsMap = new Map(element.shadowColorsMap);
    },
    { immediate: true }
);

const changeShadowType = (type: ShadowTypeEnum) => {
    const element = props.textElement as BoundedTextElement;
    if (!element) return;

    const newType = model.shadowType === type ? null : type;
    model.shadowType = newType;
    element.set('shadowType', newType);

    const isBlurred = newType === ShadowTypeEnum.Blurred;
    const isCleared = newType == null;

    // Sync model and element
    const sync = <K extends 'shadowOffset' | 'shadowAngle' | 'shadowBlur'>(
        prop: K,
        value: number
    ) => {
        element.set(prop, value);
        model[prop] = value;
    };

    if (isCleared) {
        // Clear values
        sync('shadowOffset', 0);
        sync('shadowAngle', 0);
        sync('shadowBlur', 0);
    } else {
        // Set default values
        if (model.shadowOffset === 0) sync('shadowOffset', defaultOffset);
        if (model.shadowAngle === 0) sync('shadowAngle', defaultAngle);

        // Set blur
        if (isBlurred && model.shadowBlur === 0) {
            sync('shadowBlur', defaultBlur);
        } else if (!isBlurred && model.shadowBlur !== 0) {
            sync('shadowBlur', 0);
        }
    }

    if (props.editMode) {
        ['shadowType', 'shadowOffset', 'shadowAngle', 'shadowBlur'].forEach(prop =>
            element.setEditedProp(prop)
        );
    }

    props.canvas.requestRenderAll();
};

const changeShadowOffset = () => {
    const element = props.textElement as BoundedTextElement;
    if (!element) return;

    element.set('shadowOffset', model.shadowOffset);

    if (props.editMode) {
        element.setEditedProp('shadowOffset');
    }
    props.canvas.requestRenderAll();
};

const changeShadowAngle = () => {
    const element = props.textElement as BoundedTextElement;
    if (!element) return;

    element.set('shadowAngle', model.shadowAngle);

    if (props.editMode) {
        element.setEditedProp('shadowAngle');
    }
    props.canvas.requestRenderAll();
};

const changeShadowBlur = () => {
    const element = props.textElement as BoundedTextElement;
    if (!element) return;

    element.set('shadowBlur', model.shadowBlur);

    if (props.editMode) {
        element.setEditedProp('shadowBlur');
    }
    props.canvas.requestRenderAll();
};

const onShadowColorSelected = async (value: ElementMapOption, option = 1) => {
    const element = props.textElement as BoundedTextElement;
    if (!element) return; 
    const color = value as ColorObject;
    if (!color) return;

    if (props.editMode) {
        element.setEditedProp('shadowColorsMap', 'shadowColor');
    }

    element.shadowColorsMap.set(option, color);
    model.shadowColorsMap = new Map(element.shadowColorsMap);
    if (option == 1) {
        element.shadowColor = color;
    }

    loadColorIntoElement(option, undefined);
};

const previewColorOption = (color?: ElementMapOption) => {
    loadColorIntoElement(model.shadowColorOption || 1, color);
};

const removeColorOptionFromMap = (position: number) => {
    const element = props.textElement as BaseTextElement;
    if (!element) return;
  
    if (props.editMode) {
        element.setEditedProp('shadowColorsMap', 'shadowColor');
    }
    element.shadowColorsMap.delete(position);
    model.shadowColorsMap.delete(position);
    loadColorIntoElement(1, undefined);
    model.shadowColorsMap = new Map(model.shadowColorsMap);
};

const { hexToRgba } = useColor();
const loadColorIntoElement = (option: number, value?: ElementMapOption) => {
    const element = props.textElement as BaseTextElement;
    if (!element) return;

    const colorMap = element.shadowColorsMap;
    const color = (value as ColorObject) || colorMap.get(option);

    if (!color) return;

    const rgba = color.alpha < 1 ? hexToRgba(color.hex, color.alpha) : color.hex;
    
    element.setShadowColor(rgba);
    element.shadowColorOption = model.shadowColorOption = option;

    props.canvas.requestRenderAll();
};

const onColorOptionsReordered = (
    options: KeyValueDto<number, ElementMapOption>[]
) => {
    const element = props.textElement as BaseTextElement;
    if (!element) return;

    const colorMap = new Map<number, FontColorOption>();
    options.forEach(({ value }, index) => {
        const position = index + 1;
        colorMap.set(position, value as FontColorOption);
    });
    if (props.editMode) {
        element.setEditedProp('shadowColorsMap', 'shadowColor');
    }
    element.shadowColorsMap = colorMap;
    model.shadowColorsMap = new Map(colorMap);
    loadColorIntoElement(1, undefined);
};

const pickColorLibrary = () => {
    const element = props.textElement as BaseTextElement;
    if (!element) return;

    element.shadowColorOption= model.shadowColorOption = 1;
    emit('open-shadow-color-library-selector');
}


const removeColorLibrary = () => {
    const element = props.textElement as BaseTextElement;
    if (!element) return;

    element.shadowColorLibraryId = undefined;
    if (props.editMode) {
        element.setEditedProp('shadowColorLibraryId');
    }
    loadColorIntoElement(1, undefined);

    model.shadowColorLibraryId = undefined;
};


return { __sfc: true,disabled, model, COLOR_TYPE, DROP_SHADOW, BLURRED_SHADOW, defaultOffset, defaultAngle, defaultBlur, shadowOptions, props, emit, changeShadowType, changeShadowOffset, changeShadowAngle, changeShadowBlur, onShadowColorSelected, previewColorOption, removeColorOptionFromMap, hexToRgba, loadColorIntoElement, onColorOptionsReordered, pickColorLibrary, removeColorLibrary, CSlider, ElementLibrarySelector }
}

})